import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRouterActions } from 'react-resource-router-next';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import Link from '@atlaskit/link/link';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { HARDWARE_ROUTE_PATH } from './constants';
import { messages } from './messages';

const isOnHardwareRoute = (): boolean =>
	typeof window !== 'undefined' && window.location.pathname.endsWith(HARDWARE_ROUTE_PATH);

type HamOnboardingHardwareLinkProps = ActionRendererFor<
	typeof ActionKeys.HamOnboardingSetupComplete | typeof ActionKeys.HamOnboardingSetupCompleteSkill
>;

export const HamOnboardingHardwareLink = ({
	actions,
	conversationId,
	messageId,
}: HamOnboardingHardwareLinkProps): React.JSX.Element | null => {
	di(useIntl, useRouterActions, useAnalytics);

	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const { sendAnalyticsEvent } = useAnalytics();
	const hasTrackedViewedRef = useRef(false);

	const isRendered = actions.length > 0;

	useEffect(() => {
		if (!isRendered || hasTrackedViewedRef.current) {
			return;
		}
		hasTrackedViewedRef.current = true;

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'hamOnboardingHardwareLink',
			actionSubjectId: 'hamEnabled',
			attributes: {
				conversationId: conversationId ?? '',
				messageId: messageId ?? '',
			},
		});
	}, [conversationId, isRendered, messageId, sendAnalyticsEvent]);

	if (!isRendered) {
		return null;
	}

	// On the hardware route the link re-enters the current route so the Jira/JSM SPA
	// refetches its data without a full browser reload; elsewhere it navigates to hardware.
	const handleClick = (
		event: React.MouseEvent<HTMLAnchorElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'hardwareLink',
				attributes: {
					...analyticsEvent.payload?.attributes,
					conversationId: conversationId ?? '',
					messageId: messageId ?? '',
				},
			}),
		);

		if (isOnHardwareRoute()) {
			event.preventDefault();
			push(`${window.location.pathname}${window.location.search}`);
		}
	};

	return (
		<Stack space="space.100">
			<Text>{formatMessage(messages.leadInText)}</Text>
			<Link
				href={HARDWARE_ROUTE_PATH}
				onClick={handleClick}
				testId="ham-onboarding-hardware-link.link"
			>
				{formatMessage(messages.hardwareLinkLabel)}
			</Link>
		</Stack>
	);
};
